import { useSelector } from 'react-redux';

/**
 * Checks whether an organization is entitled to Market Research Recommendations.
 * Entitlement requires BOTH Accessibility ('recommendations') and
 * Visibility ('recommendations-visibility') under the 'discovery' module.
 *
 * @param {Array} permissions - Array of module objects or flattened permission strings.
 * @returns {boolean} True if entitled, false otherwise (fail-closed).
 */
export const hasRecommendationsEntitlement = (permissions) => {
  if (!permissions || !Array.isArray(permissions) || permissions.length === 0) {
    return false;
  }

  // Handle flattened array of submodule permission strings
  if (typeof permissions[0] === 'string') {
    const permSet = new Set(permissions);
    return permSet.has('recommendations') && permSet.has('recommendations-visibility');
  }

  // Handle array of module objects: [{ key: 'discovery', submodules: [...] }]
  const discoveryModule = permissions.find((m) => m?.key === 'discovery');
  const subs = Array.isArray(discoveryModule?.submodules)
    ? discoveryModule.submodules
    : permissions.flatMap((m) => (Array.isArray(m?.submodules) ? m.submodules : []));

  const allSubs = new Set(subs);
  return allSubs.has('recommendations') && allSubs.has('recommendations-visibility');
};

/**
 * React hook to retrieve recommendations entitlement status from Redux orgSettings.
 * Safely falls back to true if orgSettings is not in store (e.g. focused unit tests).
 * @returns {boolean}
 */
export const useRecommendationsEntitled = () => {
  const permissions = useSelector((state) => {
    if (!state || !('orgSettings' in state)) {
      return null;
    }
    return state.orgSettings?.permissions || [];
  });

  if (permissions === null) {
    return true;
  }

  return hasRecommendationsEntitlement(permissions);
};
